import { useBodyScrollLock } from "@brexhq/metal-utils";
import styled from "@emotion/styled";
import { AnimatePresence, m } from "framer-motion";
import { padding } from "polished";
import React, { useState } from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";

import type { INavigation, INavMenu, INavGroup } from "../../types";

import BottomLinks from "./BottomLinks";
import NavGroup from "./NavGroup";
import NavMenu from "./NavMenu";
import RootMenu from "./RootMenu";

import { MobileBottomCtasStencil, MobileMenuLoadingStencil } from "./";

const Backdrop = styled(m.div)(({ theme }) => ({
  position: "fixed",
  background: "rgba(0,0,0,0.3)",
  inset: 0,
  zIndex: theme.zIndex.modal - 1,
}));

const PageContainer = styled(m.div)(() => ({
  height: "100%",
  display: "grid",
  gridColumn: 1,
  gridRow: 1,
  gridTemplateRows: "50px 1fr",
}));

const MenuContainer = styled(m.div)(({ theme }) => ({
  position: "fixed",
  top: 0,
  bottom: 0,
  display: "flex",
  flexDirection: "column",
  background: theme.colors.background.primary,
  width: "90vw",
  zIndex: theme.zIndex.modal,
  overflowX: "hidden",
}));

const BackLinkContainer = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  ...padding(0, theme.spacing.SML.sml),
}));

const BackLink = styled.button(({ theme }) => ({
  ...theme.typography.BodySmallSB,
  color: theme.colors.accent.primary.background.default,
  background: "none",
  border: "none",
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  gap: theme.spacing.SML.xxsml,
  padding: 0,
  margin: 0,
}));

const getPageTitle = (page: PageType): string => {
  if (page === "root") {
    return "Main menu";
  }
  if (page._type === "navMenu") {
    return page.text;
  }
  return page.title.text;
};

type PageType = "root" | INavMenu | INavGroup;

interface Props {
  className?: string;
  data: INavigation;
  isOpen: boolean;
  onClose: () => void;
  loading?: boolean;
  darkModeNav?: boolean;
}

export default function Menu({
  className,
  data: { left, right },
  isOpen,
  onClose,
  loading,
  darkModeNav,
}: Props) {
  const [parentPageStack, setParentPageStack] = useState<PageType[]>([]);
  const [currentPage, setCurrentPage] = useState<PageType>("root");
  const [direction, setDirection] = useState<"left" | "right">("right");

  const closeMenu = () => {
    setCurrentPage("root");
    setParentPageStack([]);
    onClose();
  };

  useBodyScrollLock(isOpen);

  const Content = (
    <>
      {currentPage === "root" ? (
        <RootMenu
          items={left}
          onPageChange={(page) => {
            setDirection("right");
            setParentPageStack([currentPage, ...parentPageStack]);
            setCurrentPage(page);
          }}
        />
      ) : currentPage._type === "navMenu" ? (
        <NavMenu
          data={currentPage}
          onPageChange={(page) => {
            setDirection("right");
            setParentPageStack([currentPage, ...parentPageStack]);
            setCurrentPage(page);
          }}
        />
      ) : (
        <NavGroup data={currentPage} />
      )}
    </>
  );
  const MenuContent = (
    <>
      <Container
        height="100%"
        display="grid"
        overflowY="auto"
        overflowX="hidden"
      >
        <AnimatePresence>
          <PageContainer
            key={getPageTitle(currentPage)}
            custom={direction}
            variants={{
              enter: (direction: "left" | "right") => ({
                x: direction === "left" ? "100%" : "-100%",
                opacity: 0,
              }),
              center: {
                x: 0,
                opacity: 1,
                zIndex: 1,
              },
              exit: (direction: "left" | "right") => ({
                x: direction === "left" ? "-100%" : "100%",
                opacity: 0,
                zIndex: 0,
              }),
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 400, damping: 40 },
              opacity: { duration: 0.2 },
            }}
          >
            <BackLinkContainer>
              {parentPageStack.length > 0 ? (
                <BackLink
                  onClick={() => {
                    const [parentPage, ...stack] = parentPageStack;
                    setDirection("left");
                    setParentPageStack(stack);
                    setCurrentPage(parentPage);
                  }}
                >
                  <svg
                    width="6"
                    height="10"
                    viewBox="0 0 6 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M2.18557e-07 5L4.87188 10L6 9.14938L1.95694 5L6 0.850619L4.87188 -2.12957e-07L2.18557e-07 5Z"
                      fill="currentColor"
                    />
                  </svg>
                  {getPageTitle(parentPageStack[0])}
                </BackLink>
              ) : (
                <BackLink onClick={() => closeMenu()}>Close</BackLink>
              )}
            </BackLinkContainer>
            {loading ? <MobileMenuLoadingStencil /> : Content}
          </PageContainer>
        </AnimatePresence>
      </Container>
      {loading ? (
        <MobileBottomCtasStencil />
      ) : (
        <BottomLinks links={right} darkModeNav={darkModeNav} />
      )}
    </>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Backdrop
            className={className}
            role="presentation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => closeMenu()}
          />
          <MenuContainer
            className={className}
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{
              x: { type: "spring", stiffness: 400, damping: 40 },
            }}
          >
            <Container display="flex" direction="column" height="100%">
              {MenuContent}
            </Container>
          </MenuContainer>
        </>
      )}
    </AnimatePresence>
  );
}
